/**
 * <s-hscroll>: pinned horizontal gallery. The host grows tall enough for its
 * [data-hscroll-track] to slide fully across a sticky [data-hscroll-stage] while
 * the page scrolls vertically. It writes --hx (track offset) and --hp (0..1
 * progress) and sets data-active to the [data-group] of the item nearest the
 * stage centre, so CSS can morph colours between groups. Without JS or with
 * reduced motion the track is a native horizontal scroller.
 */
class SHscroll extends HTMLElement {
  #frame = 0;
  #travel = 0;
  #ro = null;
  #io = null;
  #current = null;
  #swap = 0;
  #onResize = () => this.#measure();
  #onScroll = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
  };
  // Perf (2026-10-29 audit): item centres are measured once (#measure) and shifted by the track offset, so a
  // scroll frame reads one rect and writes only when the offset changes; it used to write the offset and then
  // read 15 card rects, forcing a full layout every frame.
  #hx = 0;
  #bases = [];
  #update = () => {
    this.#frame = 0;
    const rect = this.getBoundingClientRect();
    const span = rect.height - window.innerHeight;
    const progress = span > 0 ? Math.min(Math.max(-rect.top / span, 0), 1) : 0;
    const hx = -progress * this.#travel;
    const hxText = `${hx.toFixed(1)}px`;
    if (hxText === this.#hxText) return;
    this.#hxText = hxText;
    this.#hx = hx;
    this.style.setProperty('--hx', hxText);
    this.style.setProperty('--hp', progress.toFixed(4));
    const middle = window.innerWidth / 2;
    let nearest = null;
    let best = Infinity;
    this.items.forEach((item, i) => {
      const distance = Math.abs((this.#bases[i] ?? 0) + hx - middle);
      if (distance < best) { best = distance; nearest = item; }
    });
    const group = nearest?.dataset.group;
    if (group && this.dataset.active !== group) this.dataset.active = group;
    if (nearest) this.#spotlight(nearest);
  };
  #hxText = '';

  connectedCallback() {
    this.track = this.querySelector('[data-hscroll-track]');
    this.items = [...this.querySelectorAll('[data-group]')];
    this.themes = this.items.filter((item) => item.hasAttribute('data-theme'));
    this.nameEl = this.querySelector('[data-hs-name]');
    this.countEl = this.querySelector('[data-hs-count]');
    if (!this.track) return;
    // Fix (2026-11-03, merchant: theme images missing while scrolling): the track moves by transform, so
    // native lazy loading (Safari especially) never sees off-screen cards enter. Once the section is within
    // one screen, every gallery image switches to eager and loads before its card slides in.
    this.#io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      this.#io.disconnect();
    }, { rootMargin: '100% 0px' });
    this.#io.observe(this);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.setAttribute('data-live', '');
    this.#ro = new ResizeObserver(() => this.#measure());
    this.#ro.observe(this.track);
    this.#measure();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    this.#io?.disconnect();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#swap);
    this.#frame = 0;
  }

  // The card nearest the centre lights the stage with its tone (--hs-glow), shows its name huge behind the
  // track (cross-faded via data-swap) and updates the "03 / 15" counter.
  #spotlight(item) {
    if (this.#current === item) return;
    this.#current?.removeAttribute('data-lit');
    this.#current = item;
    item.setAttribute('data-lit', '');
    if (item.dataset.glow) this.style.setProperty('--hs-glow', item.dataset.glow);
    this.setAttribute('data-glowing', '');
    const index = this.themes.indexOf(item);
    if (this.countEl && index >= 0) {
      const pad = (n) => String(n).padStart(2, '0');
      this.countEl.textContent = `${pad(index + 1)} / ${pad(this.themes.length)}`;
    }
    const name = item.dataset.name || '';
    if (!this.nameEl || this.nameEl.textContent === name) return;
    clearTimeout(this.#swap);
    this.nameEl.setAttribute('data-swap', '');
    this.#swap = setTimeout(() => {
      this.nameEl.textContent = name;
      this.nameEl.removeAttribute('data-swap');
    }, 240);
  }

  #measure() {
    this.#travel = Math.max(this.track.scrollWidth - window.innerWidth, 0);
    // The page scrolls ~30% less than the track travels (merchant: the list took too long to scroll).
    this.style.height = `${Math.round(this.#travel / 1.3) + window.innerHeight}px`;
    // Item centres with the current track offset taken out (the offset is a transform, so it moves them rigidly).
    this.#bases = this.items.map((item) => {
      const box = item.getBoundingClientRect();
      return box.left + box.width / 2 - this.#hx;
    });
    this.#hxText = '';
    this.#update();
  }
}

if (!customElements.get('s-hscroll')) customElements.define('s-hscroll', SHscroll);
